/**
 * Assessment result card: the level that adjusts the inherent risk, with each candidate
 * assessment as a detail row linking through to the Assessments tab.
 */
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import RatingBar from '@app/src/components/RatingBar'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { stringifyQueryFilters } from '@app/src/hooks/queryState'
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import { AllTabs } from '@app/src/pages/ResourceExplorer/Content'
import { ActivateInConfigurationButton } from '@app/src/pages/TopicalRisk/Drawer/ActivateInConfigurationButton'
import { CollapsibleRiskCard } from '@app/src/pages/TopicalRisk/Drawer/CollapsibleRiskCard'
import type { AssessmentRow } from '@app/src/pages/TopicalRisk/Drawer/assessmentResultLogic'
import {
  buildRowName,
  findAssessmentForRow,
  hasAssessmentData,
  hasDeeperAssessmentAvailable,
  orderAssessmentRowsForDisplay,
} from '@app/src/pages/TopicalRisk/Drawer/assessmentResultLogic'
import { RiskCardListItem } from '@app/src/pages/TopicalRisk/Drawer/RiskCardListItem'
import { useEffectiveAssessment } from '@app/src/pages/TopicalRisk/Drawer/useEffectiveAssessment'
import type Assessment from '@app/src/types/assessment'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'
import { AmplitudeTrackingEvents } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { OpenInNewOutlined } from '@mui/icons-material'
import { Box } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import { useHistory } from 'react-router-dom'

const buildAssessmentsPath = (providerId: number, assessmentTemplateId: number) =>
  stringifyQueryFilters({
    url: generatePath(paths.resourceExplorer, {
      resourceType: 'supplier',
      resourceId: providerId,
      activeTabParam: AllTabs.Assessments,
    }),
    queryParams: {
      filters: [{ name: 'assessmentTemplate.id', value: assessmentTemplateId.toString(), operator: Operators.EqualTo }],
    },
  })

const RatingBarSlot: React.FC<{ level: number; levelsCount?: number }> = ({ level, levelsCount }) => (
  <Box sx={{ width: '83px' }}>
    <RatingBar level={level} levelsCount={levelsCount} />
  </Box>
)

/** A row that is not activated offers the way to turn it on rather than reporting a result. */
function renderRowTrailing(row: AssessmentRow): React.ReactNode {
  if (!row.isActivated)
    return (
      <ActivateInConfigurationButton
        configurationsPage={CONFIGURATION_PAGE_IDS.Assessments}
        noAccessTooltipId="topicalRisk.drawer.activateAssessmentNoAccess"
      />
    )
  // "Not enough public data" only renders for topic risks stored before AI screening was capped at
  // inherent risk (see isAssessmentExcludedDueToLowData). Remove it together with that flag.
  if (row.isExcludedDueToLowData)
    return <SimpleRiskChip outlined riskStatus={RiskStatus.InsufficientData} showBackgroundForInactive />
  if (!row.level) return <SimpleRiskChip outlined riskStatus={RiskStatus.NotApplicable} showBackgroundForInactive />

  return <RatingBarSlot level={row.level} levelsCount={row.levelsCount} />
}

export interface AssessmentResultCardProps {
  sustainabilityTopicRisk: SustainabilityTopicRisk
}

export const AssessmentResultCard: React.FC<AssessmentResultCardProps> = ({ sustainabilityTopicRisk }) => {
  const { formatMessage, formatDate } = useIntl()
  const { closeDrawer } = useDrawer()
  const { trackEvent } = useAmplitude()
  const history = useHistory()
  const { rows, effectiveRow, effectiveAssessment, assessments, isLoadingAssessments } =
    useEffectiveAssessment(sustainabilityTopicRisk)
  const displayRows = useMemo(() => orderAssessmentRowsForDisplay(rows, effectiveRow), [rows, effectiveRow])

  const navigateToAssessment = (row: AssessmentRow) => {
    if (!row.assessmentTemplateId) return

    trackEvent({
      name: AmplitudeTrackingEvents.Analyze.SupplierRisk.ClickedAssessment,
      eventProps: {
        providerId: sustainabilityTopicRisk.providerId,
        assessmentTemplateId: row.assessmentTemplateId,
      },
    })
    closeDrawer()
    history.push(buildAssessmentsPath(sustainabilityTopicRisk.providerId, row.assessmentTemplateId))
  }

  const getSupportingText = (row: AssessmentRow, assessment?: Assessment) => {
    if (!row.isActivated) return formatMessage({ id: 'schemas.risk.status.GDeactivated' })
    if (row.isExcludedDueToLowData) return formatMessage({ id: 'topicalRisk.notEnoughPublicData' })
    // Avoid flashing "No data" while the reporting date is still being fetched.
    if (isLoadingAssessments) return ''
    if (!assessment) return formatMessage({ id: 'schemas.risk.status.ANotApplicable' })

    return formatMessage(
      { id: assessment.isScreened ? 'topicalRisk.drawer.aiScreened' : 'topicalRisk.drawer.selfReported' },
      { date: formatDate(assessment.updatedAt, { year: 'numeric', month: 'long', day: 'numeric' }) },
    )
  }

  return (
    <CollapsibleRiskCard
      title={formatMessage({ id: 'topicalRisk.drawer.assessmentResult' })}
      statusElement={
        effectiveRow?.level ? (
          <RatingBarSlot level={effectiveRow.level} levelsCount={effectiveRow.levelsCount} />
        ) : (
          <SimpleRiskChip larger outlined riskStatus={RiskStatus.NotApplicable} showBackgroundForInactive />
        )
      }
      description={
        effectiveRow
          ? formatMessage(
              {
                id: hasDeeperAssessmentAvailable(rows, effectiveRow)
                  ? 'topicalRisk.drawer.assessmentResultDescriptionWithDeepDive'
                  : 'topicalRisk.drawer.assessmentResultDescription',
              },
              { assessmentName: buildRowName(effectiveRow, effectiveAssessment) },
            )
          : formatMessage({ id: 'topicalRisk.noAssessmentResults' })
      }
    >
      {displayRows.length
        ? displayRows.map(row => {
            const assessment = findAssessmentForRow(row, assessments)
            // Only offer the link out once the assessment has been answered.
            const canOpenAssessment = hasAssessmentData(row) && Boolean(row.assessmentTemplateId)

            return (
              <RiskCardListItem
                key={`${row.assessmentTemplateId}-${row.name}`}
                name={buildRowName(row, assessment)}
                supportingText={getSupportingText(row, assessment)}
                icon={canOpenAssessment ? <OpenInNewOutlined fontSize="small" /> : undefined}
                iconLabel={formatMessage({ id: 'topicalRisk.viewAssessment' })}
                onIconClick={canOpenAssessment ? () => navigateToAssessment(row) : undefined}
                trailingElement={renderRowTrailing(row)}
              />
            )
          })
        : undefined}
    </CollapsibleRiskCard>
  )
}

export default AssessmentResultCard
